import { useState, useEffect } from "react";
import { Bell, X, TrendingUp, TrendingDown, Download, RefreshCw, AlertCircle, Calendar } from "lucide-react";
import { Button } from "../components/ui/button";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";

const PriceChangeNotification = () => {
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);
  const [error, setError] = useState(null);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/pricing/notifications`);
      setNotifications(response.data.notifications || []);
    } catch (err) {
      console.error("Error fetching notifications:", err);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const markAsRead = async () => {
    try {
      await axios.post(`${API}/pricing/notifications/mark-read`);
      setNotifications([]);
    } catch (err) {
      console.error("Error:", err);
    }
  };

  const checkPrices = async () => {
    setLoading(true);
    setError(null);
    const toastId = toast.loading("Fetching rates...");
    
    try {
      const response = await axios.post(`${API}/pricing/refresh`, {}, { timeout: 30000 });

      if (!response.data.success) {
        setError(response.data.error || "Failed to fetch rates");
        toast.error(response.data.error || "Failed to fetch rates", { id: toastId });
        return;
      }

      // Rate fetch runs as a background job (can take ~4 min for the full rate table).
      // Poll for progress; if it outlasts our window it keeps running server-side.
      const deadline = Date.now() + 300000; // 5 min
      let status = null;
      let completed = false;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        const s = await axios.get(`${API}/pricing/refresh-status`);
        status = s.data;
        if (status && status.running === false && (status.completed_at || status.error)) {
          completed = true;
          break;
        }
        if (status && status.total > 0) {
          toast.loading(`Checking rates… ${status.progress || 0}/${status.total}`, { id: toastId });
        }
      }

      if (status && status.error) {
        setError(status.error);
        toast.error(status.error, { id: toastId });
        return;
      }

      // Still running when we stopped polling — don't report misleading zeros
      if (!completed) {
        toast("Rate check is still running in the background. You'll be notified when it's done.", { id: toastId });
        return;
      }

      setLastChecked(new Date().toLocaleTimeString());
      const ratesCount = status?.rates_count ?? 0;
      const changesCount = status?.changes_count ?? 0;
      let msg = `Checked ${ratesCount} rates. ${changesCount} change${changesCount === 1 ? "" : "s"} found.`;
      if (status?.email_sent) msg += " Email notification sent!";
      toast.success(msg, { id: toastId });

      if (changesCount > 0) {
        fetchNotifications();
      }
    } catch (err) {
      const msg = err.code === 'ECONNABORTED' ? "Request timed out. Try again." : "Failed to check prices";
      setError(msg);
      toast.error(msg, { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    setDownloading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/pricing/pdf`, { responseType: 'blob', timeout: 30000 });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `rate-card-${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Rate card downloaded!");
    } catch (err) {
      if (err.response?.status === 404) {
        setError("No rates stored. Click 'Check Prices' first.");
        toast.error("No rates stored. Click 'Check Prices' first.");
      } else {
        setError("Download failed");
        toast.error("Download failed. Try again.");
      }
    } finally {
      setDownloading(false);
    }
  };

  const digestNotifs = notifications.filter((n) => n.type === "weekly_digest");
  const changeNotifs = notifications.filter((n) => n.type !== "weekly_digest");
  const totalChanges = changeNotifs.reduce((sum, n) => sum + (n.changes?.length || 0), 0);
  const digestTotal = digestNotifs.reduce((sum, d) => sum + (d.summary?.total ?? d.changes?.length ?? 0), 0);
  const attention = totalChanges > 0 || digestTotal > 0;
  const bellLabel = totalChanges > 0
    ? `${totalChanges} Price Changes`
    : (digestNotifs.length > 0 ? "Weekly Digest" : "Pricing");

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {!isOpen ? (
        <Button
          onClick={() => setIsOpen(true)}
          data-testid="pricing-bell-btn"
          className={attention
            ? "bg-red-500 hover:bg-red-600 text-white shadow-lg animate-pulse"
            : "bg-white hover:bg-slate-50 text-slate-700 shadow-lg border border-slate-200"}
        >
          <Bell className="w-4 h-4 mr-2" />
          {bellLabel}
        </Button>
      ) : (
        <div className="bg-white rounded-lg shadow-2xl border border-slate-200 w-80 overflow-hidden">
          {/* Header */}
          <div className="bg-[#16a34a] text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4" />
              <span className="font-medium">Price Monitoring</span>
            </div>
            <button onClick={() => setIsOpen(false)} className="hover:bg-white/20 p-1 rounded">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Actions */}
          <div className="p-3 border-b border-slate-100 space-y-2">
            <Button
              variant="outline"
              size="sm"
              onClick={checkPrices}
              disabled={loading}
              className="w-full text-sm"
              data-testid="bell-check-prices-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Checking Prices...' : 'Check Prices'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={downloadPDF}
              disabled={downloading}
              className="w-full text-sm"
            >
              <Download className={`w-4 h-4 mr-2 ${downloading ? 'animate-pulse' : ''}`} />
              {downloading ? 'Downloading...' : 'Download Rate Card PDF'}
            </Button>
            {lastChecked && (
              <p className="text-xs text-slate-400 text-center">Last checked: {lastChecked}</p>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="px-3 py-2 bg-red-50 border-b border-red-100 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-500" />
              <p className="text-xs text-red-600">{error}</p>
            </div>
          )}

          {/* Notifications */}
          <div className="max-h-[250px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-6 text-center text-slate-400">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No price changes</p>
                <p className="text-xs mt-1">Click "Check Prices" to fetch rates</p>
              </div>
            ) : (
              notifications.map((notif, idx) => {
                const isDigest = notif.type === "weekly_digest";
                return (
                  <div
                    key={idx}
                    className="border-b border-slate-100 last:border-0"
                    data-testid={isDigest ? `weekly-digest-notification` : `price-notification-${idx}`}
                  >
                    <div className={`px-3 py-1.5 flex items-center gap-1.5 ${isDigest ? "bg-emerald-50" : "bg-red-50"}`}>
                      {isDigest && <Calendar className="w-3.5 h-3.5 text-emerald-600" />}
                      <p className={`text-xs font-medium ${isDigest ? "text-emerald-700" : "text-red-600"}`}>
                        {isDigest
                          ? `Weekly Rate Digest · ${notif.summary?.period_start || ""} → ${notif.summary?.period_end || ""}`
                          : `${notif.changes?.length} change(s) - ${notif.created_at?.slice(0, 10)}`}
                      </p>
                    </div>
                    {isDigest && (
                      <div className="px-3 py-1.5 flex gap-4 text-xs bg-emerald-50/60 border-b border-emerald-100">
                        <span className="inline-flex items-center gap-1 text-red-600 font-semibold">
                          <TrendingUp className="w-3 h-3" /> {notif.summary?.up || 0} up
                        </span>
                        <span className="inline-flex items-center gap-1 text-green-600 font-semibold">
                          <TrendingDown className="w-3 h-3" /> {notif.summary?.down || 0} down
                        </span>
                      </div>
                    )}
                    {isDigest && (notif.changes?.length || 0) === 0 ? (
                      <div className="px-3 py-4 text-center text-xs text-slate-500" data-testid="weekly-digest-empty">
                        No rate changes this week 🎉
                      </div>
                    ) : (
                      notif.changes?.map((ch, i) => (
                        <div key={i} className="px-3 py-2 hover:bg-slate-50 flex justify-between items-center">
                          <div>
                            <p className="text-sm font-medium">{ch.destination}</p>
                            <p className="text-xs text-slate-500">{ch.weight}kg - {ch.service}</p>
                          </div>
                          <div className="text-right">
                            <div className="flex items-center gap-1 justify-end">
                              {ch.change > 0 ? (
                                <TrendingUp className="w-3 h-3 text-red-500" />
                              ) : (
                                <TrendingDown className="w-3 h-3 text-green-500" />
                              )}
                              <span className={`text-sm font-bold ${ch.change > 0 ? 'text-red-500' : 'text-green-500'}`}>
                                R{Math.abs(ch.change).toFixed(2)}
                              </span>
                            </div>
                            <p className="text-xs text-slate-400">
                              R{ch.old_price} → R{ch.new_price}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-3 py-2 bg-slate-50 border-t">
              <Button variant="ghost" size="sm" onClick={markAsRead} className="w-full text-xs" data-testid="clear-notifications-btn">
                Clear all notifications
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PriceChangeNotification;
